// HTTP file upload paired with the WebSocket. Same host/port/token, path /upload.
// Returns an Attachment stub the caller stuffs into a UserMessagePayload.

import type { Attachment } from '../protocol/types';

export const MAX_UPLOAD_SIZE = 50 * 1024 * 1024;

export type UploadError =
  | { kind: 'fileTooLarge'; bytes: number }
  | { kind: 'unauthorized' }
  | { kind: 'serverError'; status: number; body?: string }
  | { kind: 'malformedResponse' }
  | { kind: 'transport'; message: string };

export class NiuUploadError extends Error {
  constructor(public readonly detail: UploadError) {
    super(`Upload failed: ${detail.kind}`);
    this.name = 'NiuUploadError';
  }
}

/** Swap wss→https / ws→http; replace path with /upload. */
export function uploadURLFromWebSocketURL(wsURL: string): string {
  const u = new URL(wsURL);
  u.protocol = u.protocol === 'wss:' ? 'https:' : u.protocol === 'ws:' ? 'http:' : u.protocol;
  u.pathname = '/upload';
  u.search = '';
  u.hash = '';
  return u.toString();
}

export interface UploadOptions {
  webSocketURL: string;
  token: string;
  file: File | Blob;
  filename: string;
  mimeType: string;
  /** Optional progress callback (0-1). Uses XHR under the hood. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export async function uploadFile(opts: UploadOptions): Promise<Attachment> {
  const size = opts.file.size;
  if (size > MAX_UPLOAD_SIZE) {
    throw new NiuUploadError({ kind: 'fileTooLarge', bytes: size });
  }

  const baseUrl = uploadURLFromWebSocketURL(opts.webSocketURL);
  const url = `${baseUrl}?token=${encodeURIComponent(opts.token)}`;
  const encodedName = encodeURIComponent(opts.filename);

  return new Promise<Attachment>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', opts.mimeType);
    xhr.setRequestHeader('X-Filename', encodedName);
    xhr.setRequestHeader('Authorization', `Bearer ${opts.token}`);

    if (opts.onProgress && xhr.upload) {
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) opts.onProgress!(ev.loaded / ev.total);
      };
    }

    if (opts.signal) {
      const onAbort = () => {
        xhr.abort();
        reject(new NiuUploadError({ kind: 'transport', message: 'aborted' }));
      };
      if (opts.signal.aborted) {
        onAbort();
        return;
      }
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }

    xhr.onerror = () => {
      reject(new NiuUploadError({ kind: 'transport', message: 'network error' }));
    };
    xhr.ontimeout = () => {
      reject(new NiuUploadError({ kind: 'transport', message: 'timeout' }));
    };
    xhr.onload = () => {
      const status = xhr.status;
      if (status === 200) {
        try {
          const raw = JSON.parse(xhr.responseText) as Record<string, unknown>;
          if (
            typeof raw.id === 'string' &&
            typeof raw.name === 'string' &&
            typeof raw.mimeType === 'string' &&
            typeof raw.size === 'number'
          ) {
            resolve({
              id: raw.id,
              name: raw.name,
              mimeType: raw.mimeType,
              size: raw.size,
              url: typeof raw.url === 'string' ? raw.url : null,
              pageCount: typeof raw.pageCount === 'number' ? raw.pageCount : undefined,
              previewUrl: typeof raw.previewUrl === 'string' ? raw.previewUrl : null,
            });
          } else {
            reject(new NiuUploadError({ kind: 'malformedResponse' }));
          }
        } catch {
          reject(new NiuUploadError({ kind: 'malformedResponse' }));
        }
      } else if (status === 401) {
        reject(new NiuUploadError({ kind: 'unauthorized' }));
      } else if (status === 413) {
        reject(new NiuUploadError({ kind: 'fileTooLarge', bytes: size }));
      } else {
        reject(
          new NiuUploadError({
            kind: 'serverError',
            status,
            body: xhr.responseText?.slice(0, 500),
          }),
        );
      }
    };

    xhr.send(opts.file);
  });
}

/** Fetch an attachment's bytes using the WSS-derived host + bearer auth. */
export async function downloadAttachment(opts: {
  webSocketURL: string;
  token: string;
  attachmentURL: string;
}): Promise<Blob> {
  const wsBase = new URL(opts.webSocketURL);
  const httpProto = wsBase.protocol === 'wss:' ? 'https:' : 'http:';
  const target = opts.attachmentURL.startsWith('http')
    ? new URL(opts.attachmentURL)
    : new URL(opts.attachmentURL, `${httpProto}//${wsBase.host}`);

  const res = await fetch(target.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${opts.token}` },
  });
  if (!res.ok) {
    throw new NiuUploadError({ kind: 'serverError', status: res.status });
  }
  return res.blob();
}
